// SINGLE SOURCE OF TRUTH for every company fact the site states.
//
// Rules (Phase 0 of the recovery plan, report section 8):
//   - Every component, JSON-LD block and the llms.txt generator reads from
//     here. Never hard-code one of these values anywhere else.
//   - `null` means "not confirmed by the owner". Callers MUST omit the field
//     or sentence when a value is null. Never print a placeholder, "[CONFIRM]",
//     or an invented number.
//   - To publish a fact: replace the `null` with the owner-approved value.
//     Nothing else needs to change.
//
// Values that are not null below were already published on the live site
// before Phase 0 (address, contact details, profile URLs, leadership,
// certifications) and are carried over unchanged. They are not new claims.

export type OwnerPlan = {
  /** Plan name as shown on /for-business-owners */
  name: string
  /** USD per month */
  monthly: number
}

export type Leader = {
  id: string
  name: string
  jobTitle: string
  linkedin: string
  image: string
}

export const FACTS = {
  brand: {
    name: 'GHL Prime',
    legalName: 'GHL Prime LLC',
    url: 'https://ghlprime.com',
    logo: 'https://ghlprime.com/logo.png',
    email: 'info@ghlprime.com',
    phone: '+1-505-207-5189',
    // Owner 2026-11-04: describe the whole operations team, not GoHighLevel only.
    description:
      'Operations and automation team for business owners and agencies, specialising in GoHighLevel: CRM setup, workflow automation (GoHighLevel, n8n, Make, Zapier), AI chat and voice agents, websites, custom SaaS and apps, A2P 10DLC, SaaS Mode and 24/7 white-label support.',
  },

  // What the team does beyond GoHighLevel (owner 2026-11-04: "don't add only
  // GHL"). Every line is backed by a live service page or a client case study
  // (data/clientStories.ts); `url` is that proof.
  capabilities: [
    { area: 'GoHighLevel', detail: 'account setup, pipelines, calendars, workflows, snapshots, SaaS Mode, A2P 10DLC registration and migrations', url: '/services/ghl-setup' },
    { area: 'Automation beyond GoHighLevel', detail: 'n8n, Make and Zapier workflows, and API integrations between the tools a business already uses', url: '/services/api-integrations' },
    { area: 'AI agents', detail: 'chatbots that engage and qualify leads, Voice AI receptionists that answer and route calls, and AI assistants inside internal portals', url: '/services/ai-agent-builder' },
    { area: 'Websites and funnels', detail: 'WordPress, Shopify and GoHighLevel websites, sales funnels and landing pages, connected to the CRM', url: '/clients' },
    { area: 'Custom software', detail: 'custom CRMs, white-label SaaS platforms, client portals, dashboards, and web and mobile apps', url: '/services/custom-saas-development' },
    { area: 'Marketing operations', detail: 'email campaigns, webinars, Meta ads and graphic design, run for the client', url: '/clients/glscapes' },
    { area: 'People', detail: 'a dedicated full-time or part-time specialist, and 24/7 white-label client support for agencies', url: '/hire-an-individual' },
  ] as { area: string; detail: string; url: string }[],

  address: {
    streetAddress: '4801 Lang Ave NE, Suite 110',
    addressLocality: 'Albuquerque',
    addressRegion: 'NM',
    postalCode: '87109',
    addressCountry: 'US',
  },

  areaServed: ['US', 'CA', 'GB', 'AU'],

  // ---- Owner-confirmed 2026-10-29 unless marked otherwise -----------------
  // (content/ghl-prime-facts.md records the answers.)
  /** ISO date or year, e.g. "2023". null = omit everywhere. */
  foundingDate: null as string | null,
  /** Parent company. Only set once ownership is confirmed. null = omit.
   *  Set 2026-10-28: the About page states it publicly ("Backed by Octopi
   *  Digital"), and the owner's 2026-10-26 SEO audit asked for it in schema. */
  parentOrganization: { name: 'Octopi Digital', url: 'https://octopi-digital.com/' } as { name: string; url: string } | null,
  /** Total people. null = omit. */
  teamSize: 15 as number | null,
  /** GoHighLevel Certified Admins on the team (an exact count). null = omit. */
  certifiedExperts: 4 as number | null,
  /** GoHighLevel accounts and sub-accounts set up or worked in. null = omit. */
  accountsDelivered: 1000 as number | null,
  /** Schema.org priceRange. null = omit. */
  priceRange: '$10-$20 per hour; projects from $147' as string | null,
  /** Published response-time promise. null = omit. */
  responseTime: 'Within 1 hour, 24/7' as string | null,
  /** Plain statement of where the delivery team sits. null = omit. The US
   *  address above is the legal entity (contracts, billing, some sales). */
  deliveryLocation: 'Delivery team in Basundhara Residential Area, Dhaka, Bangladesh' as string | null,

  // ---- Support coverage (already stated across the live site) ------------
  supportCoverage: '24/7 white-label client support',

  // ---- Leadership (already published on the live site) -------------------
  leaders: [
    {
      id: 'jewel-rana',
      name: 'Jewel Rana',
      jobTitle: 'CEO & Co-Founder',
      linkedin: 'https://www.linkedin.com/in/thejewelrana',
      image: 'https://ghlprime.com/images/team/jewel-rana.webp',
    },
    {
      id: 'niyamul-islam-sajal',
      name: 'Niyamul Islam Sajal',
      jobTitle: 'COO & Co-Founder',
      linkedin: 'https://www.linkedin.com/in/niyamulislam',
      image: 'https://ghlprime.com/images/team/niyamul-islam-sajal.webp',
    },
  ] as Leader[],

  // ---- Profiles (only URLs already published on the live site) -----------
  // Add Clutch / G2 / Crunchbase / YouTube here ONLY once the profile exists.
  sameAs: [
    'https://www.linkedin.com/company/ghl-prime-llc',
    'https://www.facebook.com/people/GHL-PRIME-LLC/61573474861100/',
    'https://www.upwork.com/agencies/ghlprime/',
    'https://www.instagram.com/ghlprimellc/',
  ] as string[],

  // ---- Upwork agency profile (owner-confirmed 2026-10-29) ------------------
  // Shown on /reviews. Update the numbers and `asOf` together.
  upwork: {
    url: 'https://www.upwork.com/agencies/ghlprime/',
    badge: 'Top Rated',
    jobSuccess: '100%',
    jobs: 27,
    hours: '1,050+',
    asOf: 'October 2026',
  },

  // ---- Certifications (names as published on the live site) --------------
  certifications: [
    'GoHighLevel Certified Admin',
    'A2P 10DLC Compliance',
    'HIPAA Compliance',
    'AI Voice',
    'Funnel Builder',
    'Paid Ads',
    'WhatsApp Integration',
  ] as string[],

  // Matches the industries of the clients in data/portfolio.js.
  whoWeServe:
    'business owners and marketing agencies: local service businesses (roofing, HVAC, tree care, landscaping, home services), med spas and clinics, coaches and consultants, AI agencies, SaaS founders and online businesses',

  services: [
    { name: 'GHL Setup & Configuration', url: '/services/ghl-setup', summary: 'Sub-accounts, pipelines, calendars, A2P/Twilio and full GoHighLevel account setup.' },
    { name: 'Workflow Automation', url: '/services/automation', summary: 'Speed-to-lead, appointment reminders, pipeline automation, review requests and database reactivation.' },
    { name: 'SaaS CRM Launch', url: '/services/saas-crm', summary: 'Stripe rebilling, plan tiers and white-label SaaS Mode launch for agencies.' },
    { name: 'White-Label Support', url: '/services/white-label-support', summary: '24/7 expert client support delivered under the agency brand.' },
    { name: 'AI Agent Builder', url: '/services/ai-agent-builder', summary: 'Lead-qualifying chatbots, voice receptionists and AI call center agents.' },
    { name: 'Vibe Coding & Custom Dev', url: '/services/vibe-coding', summary: 'AI-assisted custom development and integrations beyond native GoHighLevel features.' },
    { name: 'Custom SaaS Development', url: '/services/custom-saas-development', summary: 'Client portals and dashboards built with Claude Code and Supabase.' },
    { name: 'Figma to Code', url: '/services/figma-to-code', summary: 'Pixel-perfect design-to-production-code builds.' },
    { name: 'Web & Mobile App Development', url: '/services/app-development', summary: 'iOS, Android and web apps from MVP to production.' },
    { name: 'SaaS Customer Support', url: '/services/saas-customer-support', summary: 'White-labeled ticket handling and client onboarding with 24/7 coverage.' },
    { name: 'GHL Experts for Agencies', url: '/services/ghl-experts-for-agencies', summary: 'Agency fulfilment partnership with a named certified specialist and white-labeled delivery.' },
  ] as { name: string; url: string; summary: string }[],

  // Homepage stat band. These four figures were already shown on the live
  // site (they were the targets of the old count-up animation), so they are
  // carried over unchanged. They have NOT been re-confirmed by the owner, and
  // "years in production" is in tension with the founding-date question in
  // the report. Set any of them to null to remove that tile entirely.
  homepageStats: [
    { label: 'Live products', value: 90 as number | null, suffix: '+' },
    // Owner 2026-10-29: this figure counts clients served, not end users.
    { label: 'Clients served', value: 550 as number | null, suffix: '+' },
    { label: 'Enterprise systems', value: 15 as number | null, suffix: '' },
    { label: 'Years in production', value: 7 as number | null, suffix: '+' },
  ],

  // ---- Pricing (owner-approved 2026-10-29) --------------------------------
  // While `approved` is false: /pricing renders noindex and is left out of
  // the sitemap and llms.txt. A null price is never shown.
  pricing: {
    approved: true,
    currency: 'USD',
    hourly: { min: 10, max: 20 } as { min: number; max: number } | null,
    dedicated: {
      fullTimeMonthlyFrom: 997 as number | null,
      fullTimeIncludes: 'one dedicated GoHighLevel expert plus a project manager',
      partTime: 'Custom plan, agreed on a free call',
      replacement: 'Free replacement if your expert is not the right fit',
    },
    /** One "from" price covers every fixed-scope project type below. */
    projectsFrom: 147 as number | null,
    projectTypes: [
      'GoHighLevel account setup',
      'Workflow automation',
      'SaaS Mode launch',
      'AI voice agent',
      'A2P 10DLC registration',
      'White-label support',
      'Migration to GoHighLevel',
      'Account audit and fix',
    ],
    /** Done-for-you plans on /for-business-owners. */
    ownerPlans: [
      { name: 'Front Desk', monthly: 149 },
      { name: 'Growth', monthly: 249 },
    ] as OwnerPlan[],
    terms: {
      minimum: 'One month, then cancel anytime',
      setupFee: false,
    },
  included: [
      'Certified GoHighLevel specialists',
      'White-label delivery under your brand',
      'Daily project updates in Slack, email or a shared board',
      'System walkthrough and handoff session',
    ],
    /** What no price covers (owner, 2026-10-29: services we don't offer). */
    notIncluded: [
      'Your GoHighLevel subscription and usage charges (phone, SMS, email, A2P 10DLC registration fees), billed by HighLevel to your account',
      'Ad spend or media buying: we build and run the systems, not the ad budgets',
      'Legal or compliance advice: we configure GoHighLevel for A2P 10DLC, TCPA and HIPAA requirements, but we are not lawyers',
      'Live human phone answering: our phone agents are AI voice agents',
    ],
  },
} as const

export type Facts = typeof FACTS

/** True when real, owner-approved pricing is available to publish. */
export function pricingIsPublishable(): boolean {
  const p = FACTS.pricing
  if (!p.approved) return false
  return p.hourly !== null || p.dedicated.fullTimeMonthlyFrom !== null || p.projectsFrom !== null
}

/** Price of a /for-business-owners plan, by name. */
export function ownerPlanPrice(name: string): number {
  const plan = FACTS.pricing.ownerPlans.find((p) => p.name === name)
  if (!plan) throw new Error(`Unknown owner plan: ${name}`)
  return plan.monthly
}

/**
 * Every published price in one sentence, for the glance block, llms.txt
 * and the /pricing short answer. Empty while pricing isn't publishable.
 */
export function pricingSummary(): string {
  if (!pricingIsPublishable()) return ''
  const p = FACTS.pricing
  const parts: string[] = []
  if (p.hourly) parts.push(`$${p.hourly.min}-$${p.hourly.max} per hour`)
  if (p.projectsFrom !== null) parts.push(`projects from $${p.projectsFrom.toLocaleString('en-US')}`)
  if (p.dedicated.fullTimeMonthlyFrom !== null) {
    parts.push(`a full-time dedicated expert with a project manager from $${p.dedicated.fullTimeMonthlyFrom.toLocaleString('en-US')} per month`)
  }
  const owner = Math.min(...p.ownerPlans.map((plan) => plan.monthly))
  if (Number.isFinite(owner)) parts.push(`business owner plans from $${owner} per month`)
  if (parts.length < 2) return parts.join('')
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
}

/** Stats that have a value; tiles with a null value are never rendered. */
export function visibleHomepageStats() {
  return FACTS.homepageStats.filter((s) => s.value !== null) as {
    label: string
    value: number
    suffix: string
  }[]
}


/**
 * "At a glance" lines, in display order. Lines whose value is null are
 * omitted entirely. Shared by the homepage/about block (T-9.1) and the
 * llms.txt generator (T-9.2) so the two can never disagree.
 */
export function glanceItems(): { term: string; detail: string }[] {
  const f = FACTS
  const items: { term: string; detail: string }[] = []
  items.push({
    term: 'Company overview',
    detail: 'An operations, automation and software team for business owners and agencies, specialising in GoHighLevel: CRM setup, automation in GoHighLevel, n8n, Make and Zapier, AI agents, websites, custom SaaS and apps.',
  })
  items.push({ term: 'Clients we serve', detail: f.whoWeServe.charAt(0).toUpperCase() + f.whoWeServe.slice(1) + '.' })
  items.push({ term: 'GoHighLevel services', detail: f.services.slice(0, 6).map((s) => s.name).join(', ') })
  if (f.teamSize !== null) items.push({ term: 'Team size', detail: `${f.teamSize} people` })
  if (f.certifiedExperts !== null) items.push({ term: 'GoHighLevel Certified Admins', detail: `${f.certifiedExperts}` })
  if (f.accountsDelivered !== null) items.push({ term: 'Accounts delivered', detail: `${f.accountsDelivered.toLocaleString('en-US')}+` })
  if (f.certifications.length) items.push({ term: 'GoHighLevel certifications', detail: f.certifications.join(', ') })
  if (f.leaders.length) {
    items.push({
      term: 'Leadership',
      detail: f.leaders.map((leader) => `${leader.name}, ${leader.jobTitle}`).join('; '),
    })
  }
  const marketNames: Record<string, string> = { US: 'United States', CA: 'Canada', GB: 'United Kingdom', AU: 'Australia' }
  if (f.areaServed.length) {
    items.push({ term: 'Markets served', detail: f.areaServed.map((market) => marketNames[market] || market).join(', ') })
  }
  const prices = pricingSummary()
  if (prices) items.push({ term: 'Pricing', detail: prices.charAt(0).toUpperCase() + prices.slice(1) })
  if (f.responseTime) items.push({ term: 'Response time', detail: f.responseTime })
  items.push({ term: 'Client support', detail: '24/7 white-label client support for agency clients, delivered under the agency’s brand.' })
  const loc = `${f.address.streetAddress}, ${f.address.addressLocality}, ${f.address.addressRegion} ${f.address.postalCode}, USA`
  items.push({ term: 'Locations', detail: f.deliveryLocation ? `US-registered LLC: ${loc}. ${f.deliveryLocation}.` : loc })
  items.push({ term: 'White-label delivery', detail: 'Backend GoHighLevel implementation and client support delivered under your agency’s brand.' })
  items.push({ term: 'Project workflow', detail: 'Scope and audit → build and configure → walkthrough and handoff → ongoing support.' })
  return items
}

/**
 * Confirmed headline figures as sentences (team size, certified experts,
 * accounts delivered, starting price). Empty string while all are null.
 */
export function glanceFigures(): string {
  const f = FACTS
  const parts: string[] = []
  const stats: string[] = []
  if (f.teamSize !== null) stats.push(`${f.teamSize} people`)
  if (f.certifiedExperts !== null) stats.push(`${f.certifiedExperts} GoHighLevel Certified Admins`)
  if (f.accountsDelivered !== null) stats.push(`${f.accountsDelivered.toLocaleString('en-US')}+ GoHighLevel accounts set up or managed`)
  if (stats.length) parts.push(`${stats.join(', ')}.`)
  const prices = pricingSummary()
  if (prices) parts.push(`Pricing: ${prices}.`)
  return parts.join(' ')
}

/** One-paragraph summary built only from non-null facts. */
export function glanceSummary(): string {
  const intro = `${FACTS.brand.name} is a GoHighLevel expert team and fulfillment partner for marketing agencies and business owners.`
  const figures = glanceFigures()
  return figures ? `${intro} ${figures}` : intro
}
